import React from 'react';
import { useDeleteTopicGroupModal } from '../hooks/useDeleteTopicGroupModal';
import {
  SlDialog,
  SlDialogRoot,
  SlDialogTitle,
  SlDialogCloseButton,
  SlDialogContent
} from 'src/components/BeaconV2Components/SlDialog';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { topicSettingsServerProxy } from '../serverProxy';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { GenericErrorMessage } from 'src/components/BeaconV2Components/common/CommonSnackbarMessages/GenericErrorMessage';
import { useHistory, useSnackbar } from 'src/utils/Hooks';
import { TOPIC_QUERY_KEYS } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Settings/topicQueryKeys';
import { useTopicPolling } from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/hooks/useTopicPolling';

/**
 * Modal used for deleting owned topic groups
 */
export const DeleteTopicGroupModal = () => {
  const { isOpen, closeModal, topicGroupId, topicGroupName } = useDeleteTopicGroupModal();
  const { showSnackbar } = useSnackbar();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { pollTopicGroupUpdate } = useTopicPolling();

  const { mutateAsync: deleteTopicGroup, isLoading } = useMutation({
    mutationFn: async () => {
      await topicSettingsServerProxy.deleteTopicGroup(topicGroupId);
      await topicSettingsServerProxy.invalidateCache();
    },
    onSuccess: () => {
      queryClient.invalidateQueries([TOPIC_QUERY_KEYS.all]);
      pollTopicGroupUpdate(topicGroupId, `deleting-topicGroup-${topicGroupId}`);
    }
  });

  const handleDeleteTopicGroup = async () => {
    if (isLoading) {
      return;
    }

    try {
      await deleteTopicGroup();

      const params = new URLSearchParams(history.location.search);
      params.set('tab', 'summary');
      params.set('entityType', 'searchtermlist');
      params.set('subtab', 'beacon');

      history.push(`/home?${params.toString()}`);
      showSnackbar({
        type: 'success',
        message: `You've successfully deleted ${topicGroupName}.`
      });
    } catch (error) {
      showSnackbar({
        type: 'error',
        message: <GenericErrorMessage />
      });
    } finally {
      closeModal();
    }
  };

  return (
    <SlDialogRoot>
      <SlDialog open={isOpen} onClose={() => closeModal()} width="500px" height="180px">
        <SlDialogCloseButton
          onClick={() => closeModal()}
          style={{
            height: '16px',
            width: '16px'
          }}
        />
        <SlDialogContent
          sx={{
            padding: '24px 8px 0px 24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}
        >
          <SlDialogTitle>Delete Topic Group</SlDialogTitle>
          <Text>
            Are you sure you want to delete this topic group? Once this topic group is deleted, it cannot be recovered.
          </Text>
          <Flex justifyContent="flex-end" alignItems="center" gap="md">
            <SlButton onClick={() => closeModal()}>Cancel</SlButton>
            <SlButton variant="contained" onClick={() => handleDeleteTopicGroup()}>
              {isLoading ? 'Deleting...' : 'Delete'}
            </SlButton>
          </Flex>
        </SlDialogContent>
      </SlDialog>
    </SlDialogRoot>
  );
};
